import { useState, useEffect } from 'react';
import { PlusCircle, Pencil, Trash2, Plus, X, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { CourseTemplate, Level, GroupSubject, CourseTemplateSubject, Course, AcademicYear, Pueblo, SubjectPuebloAssignment } from '../../types';
import { ConfirmDialog } from '../ConfirmDialog';
import { CustomSelect } from '../ui/CustomSelect';
import { getPueblos, getSubjectPuebloAssignments, saveSubjectPuebloAssignments } from '../../lib/db';

interface CourseTemplateManagerProps {
  templates: CourseTemplate[];
  levels: Level[];
  courses: Course[];
  academicYears: AcademicYear[];
  groupSubjects: GroupSubject[];
  isCra: boolean;
  onAdd: (template: Omit<CourseTemplate, 'id' | 'levelName'>) => void;
  onEdit: (id: string, template: Omit<CourseTemplate, 'id' | 'levelName'>) => void;
  onDelete: (id: string) => void;
  onCopy: (template: Omit<CourseTemplate, 'id' | 'levelName'>) => void;
}

interface FormSubject {
  id?: string;
  name: string;
  type: 'individual' | 'group';
  isRequired: boolean;
}

interface TemplatesByLevel {
  [levelId: string]: CourseTemplate[];
}

export function CourseTemplateManager({
  templates,
  levels,
  courses,
  academicYears,
  groupSubjects,
  isCra,
  onAdd,
  onEdit,
  onDelete,
  onCopy,
}: CourseTemplateManagerProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<CourseTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyingTemplate, setCopyingTemplate] = useState<CourseTemplate | null>(null);
  const [copyFormData, setCopyFormData] = useState({
    name: '',
    levelId: '',
    courseId: '',
    academicYear: '',
  });

  // CRA: pueblos + matriz de asignaciones
  const [pueblos, setPueblos] = useState<Pueblo[]>([]);
  const [matrixTemplateId, setMatrixTemplateId] = useState<string | null>(null);
  const [matrixAssignments, setMatrixAssignments] = useState<SubjectPuebloAssignment[]>([]);
  const [matrixSaving, setMatrixSaving] = useState(false);

  useEffect(() => {
    if (isCra) {
      getPueblos().then(setPueblos).catch(console.error);
    }
  }, [isCra]);

  const openMatrix = async (template: CourseTemplate) => {
    setMatrixTemplateId(template.id);
    try {
      const assignments = await getSubjectPuebloAssignments(template.id);
      setMatrixAssignments(assignments);
    } catch (err) {
      console.error(err);
      setMatrixAssignments([]);
    }
  };

  const toggleAssignment = (subjectId: string, puebloId: string) => {
    const exists = matrixAssignments.some(a => a.subjectId === subjectId && a.puebloId === puebloId);
    if (exists) {
      setMatrixAssignments(prev => prev.filter(a => !(a.subjectId === subjectId && a.puebloId === puebloId)));
    } else {
      setMatrixAssignments(prev => [...prev, { subjectId, puebloId }]);
    }
  };

  const saveMatrix = async () => {
    if (!matrixTemplateId) return;
    try {
      setMatrixSaving(true);
      await saveSubjectPuebloAssignments(matrixTemplateId, matrixAssignments);
      setMatrixTemplateId(null);
    } catch (err) {
      console.error(err);
    } finally {
      setMatrixSaving(false);
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    levelId: '',
    courseId: '',
    academicYear: '',
    subjects: [] as FormSubject[]
  });

  const [expandedLevels, setExpandedLevels] = useState<Set<string>>(new Set());

  // Organizar templates por nivel
  const templatesByLevel = templates.reduce((acc: TemplatesByLevel, template) => {
    if (!acc[template.levelId]) {
      acc[template.levelId] = [];
    }
    acc[template.levelId].push(template);
    return acc;
  }, {});

  const toggleLevel = (levelId: string) => {
    const newExpanded = new Set(expandedLevels);
    if (newExpanded.has(levelId)) {
      newExpanded.delete(levelId);
    } else {
      newExpanded.add(levelId);
    }
    setExpandedLevels(newExpanded);
  };

  const getActiveAcademicYear = () => {
    return academicYears.find(year => year.isActive)?.name || '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.levelId || !formData.courseId) {
      setError('Por favor, completa todos los campos obligatorios');
      return;
    }

    if (formData.subjects.length === 0) {
      setError('Debes añadir al menos una asignatura');
      return;
    }

    const template = {
      name: formData.name.trim(),
      levelId: formData.levelId,
      courseId: formData.courseId,
      academicYear: getActiveAcademicYear(),
      subjects: formData.subjects.map(subject => ({
        ...subject,
        id: subject.id || crypto.randomUUID()
      })) as CourseTemplateSubject[]
    };

    if (editingId) {
      onEdit(editingId, template);
    } else {
      onAdd(template);
    }

    setFormData({ name: '', levelId: '', courseId: '', academicYear: '', subjects: [] });
    setIsAdding(false);
    setEditingId(null);
    setError(null);
  };

  const handleEdit = (template: CourseTemplate) => {
    setFormData({
      name: template.name,
      levelId: template.levelId,
      courseId: template.courseId,
      academicYear: template.academicYear,
      subjects: template.subjects
    });
    setEditingId(template.id);
    setIsAdding(true);
  };

  const addSubject = (type: 'individual' | 'group') => {
    setFormData(prev => ({
      ...prev,
      subjects: [...prev.subjects, { name: '', type, isRequired: true }]
    }));
  };

  const removeSubject = (index: number) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.filter((_, i) => i !== index)
    }));
  };

  const handleCopy = (template: CourseTemplate) => {
    setCopyingTemplate(template);
    setCopyFormData({
      name: `${template.name} (copia)`,
      levelId: template.levelId,
      courseId: template.courseId,
      academicYear: template.academicYear,
    });
  };

  const handleConfirmCopy = () => {
    if (copyingTemplate && copyFormData.name.trim() && copyFormData.levelId) {
      onCopy({
        name: copyFormData.name.trim(),
        levelId: copyFormData.levelId,
        courseId: copyFormData.courseId,
        academicYear: copyFormData.academicYear,
        subjects: copyingTemplate.subjects.map(s => ({
          ...s,
          id: crypto.randomUUID()
        }))
      });
      setCopyingTemplate(null);
      setCopyFormData({ name: '', levelId: '', courseId: '', academicYear: '' });
    }
  };

  return (
    <div className="space-y-4">
      {/* Cabecera */}
      <div className="flex justify-between items-center pb-4 border-b">
        <h2 className="text-xl font-semibold text-gray-900">Plantillas de Curso</h2>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            title="Añadir nueva plantilla"
          >
            <PlusCircle className="mr-2 w-5 h-5" />
            Nueva Plantilla
          </button>
        )}
      </div>

      {/* Contenedor principal */}
      <div className="bg-white rounded-lg shadow">
        {/* Formulario de añadir/editar */}
        {isAdding && (
          <form onSubmit={handleSubmit} className="p-4 space-y-4 bg-gray-50 rounded-md">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="templateName" className="block text-sm font-medium text-gray-700">
                  Nombre de la Plantilla
                </label>
                <input
                  id="templateName"
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Introduce el nombre de la plantilla"
                  aria-label="Nombre de la plantilla"
                />
              </div>
              <div>
                <label htmlFor="levelSelect" className="block text-sm font-medium text-gray-700">
                  Nivel
                </label>
                <div className="mt-1">
                  <CustomSelect
                    value={formData.levelId}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, levelId: value }))}
                    options={levels.map(l => ({ value: l.id, label: l.name }))}
                    placeholder="Selecciona un nivel"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="courseSelect" className="block text-sm font-medium text-gray-700">
                  Curso
                </label>
                <div className="mt-1">
                  <CustomSelect
                    id="courseSelect"
                    value={formData.courseId}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, courseId: v }))}
                    options={courses.map(c => ({ value: c.id, label: c.name }))}
                    placeholder="Selecciona un curso"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Año Académico
                </label>
                <div className="mt-1 p-2 bg-gray-50 rounded-md border border-gray-300">
                  <span className="text-gray-700">{getActiveAcademicYear()}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-gray-700">Asignaturas</h3>
                <div className="space-x-2">
                  <button
                    type="button"
                    onClick={() => addSubject('individual')}
                    className="inline-flex items-center px-3 py-1 text-sm text-indigo-600 rounded-md border border-indigo-600 hover:bg-indigo-50"
                    title="Añadir asignatura individual"
                  >
                    <Plus className="mr-1 w-4 h-4" />
                    Individual
                  </button>
                  <button
                    type="button"
                    onClick={() => addSubject('group')}
                    className="inline-flex items-center px-3 py-1 text-sm text-indigo-600 rounded-md border border-indigo-600 hover:bg-indigo-50"
                    title="Añadir asignatura grupal"
                  >
                    <Plus className="mr-1 w-4 h-4" />
                    Grupal
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {formData.subjects.map((subject, index) => (
                  <div key={index} className="flex gap-3 items-center p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex-1">
                      <CustomSelect
                        value={subject.name}
                        onValueChange={(value) => {
                          const newSubjects = [...formData.subjects];
                          newSubjects[index] = { ...subject, name: value };
                          setFormData(prev => ({ ...prev, subjects: newSubjects }));
                        }}
                        options={
                          subject.type === 'group'
                            ? groupSubjects.map(gs => ({ value: gs.name, label: gs.name }))
                            : [{ value: 'Instrumento', label: 'Instrumento' }]
                        }
                        placeholder={
                          subject.type === 'individual'
                            ? '-- Selecciona asignatura individual --'
                            : '-- Selecciona asignatura grupal --'
                        }
                      />
                      <div className="flex gap-2 items-center mt-1 text-xs text-gray-500">
                        <span className={`
                          px-2 py-0.5 rounded-full
                          ${subject.type === 'individual' 
                            ? 'bg-blue-50 text-blue-700' 
                            : 'bg-green-50 text-green-700'
                          }
                        `}>
                          {subject.type === 'individual' ? 'Individual' : 'Grupal'}
                        </span>
                      </div>
                    </div>

                    <label className="flex items-center px-3 py-2 whitespace-nowrap bg-gray-50 rounded-md">
                      <input
                        type="checkbox"
                        checked={subject.isRequired}
                        onChange={e => {
                          const newSubjects = [...formData.subjects];
                          newSubjects[index] = { ...subject, isRequired: e.target.checked };
                          setFormData(prev => ({ ...prev, subjects: newSubjects }));
                        }}
                        className="text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">Obligatoria</span>
                    </label>

                    <button
                      type="button"
                      onClick={() => removeSubject(index)}
                      className="p-2 text-red-600 rounded-full transition-colors hover:text-red-900 hover:bg-red-50"
                      title={`Eliminar asignatura ${subject.name || 'sin nombre'}`}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setIsAdding(false);
                  setEditingId(null);
                  setFormData({ name: '', levelId: '', courseId: '', academicYear: '', subjects: [] });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                {editingId ? 'Guardar Cambios' : 'Crear Plantilla'}
              </button>
            </div>
          </form>
        )}

        {/* Lista de plantillas por nivel */}
        <div className="p-4 space-y-2">
          {levels.map(level => {
            const levelTemplates = templatesByLevel[level.id] || [];
            const isExpanded = expandedLevels.has(level.id);

            if (levelTemplates.length === 0) return null;

            return (
              <div key={level.id} className="overflow-hidden rounded-lg border">
                <button
                  onClick={() => toggleLevel(level.id)}
                  className="flex justify-between items-center p-4 w-full bg-gray-50 hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    {isExpanded ? (
                      <ChevronDown className="mr-2 w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="mr-2 w-5 h-5 text-gray-500" />
                    )}
                    <h3 className="text-lg font-medium text-gray-900">
                      {level.name}
                      <span className="ml-2 text-sm text-gray-500">
                        ({levelTemplates.length} plantilla{levelTemplates.length !== 1 ? 's' : ''})
                      </span>
                    </h3>
                  </div>
                </button>

                {isExpanded && (
                  <ul className="divide-y divide-gray-200">
                    {levelTemplates.map(template => (
                      <li key={template.id} className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-lg font-medium text-gray-900">{template.name}</h4>
                            <ul className="mt-2 space-y-1">
                              {template.subjects.map(subject => (
                                <li key={subject.id} className="text-sm text-gray-600">
                                  • {subject.name}
                                  {!subject.isRequired && ' (Opcional)'}
                                  <span className="text-gray-400">
                                    {' - '}{subject.type === 'individual' ? 'Individual' : 'Grupal'}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="flex space-x-2">
                            {isCra && (
                              <button
                                onClick={() => openMatrix(template)}
                                className="text-emerald-600 hover:text-emerald-900"
                                title={`Asignar pueblos a asignaturas de ${template.name}`}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                </svg>
                              </button>
                            )}
                            <button
                              onClick={() => handleCopy(template)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title={`Copiar plantilla ${template.name}`}
                            >
                              <Copy className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleEdit(template)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title={`Editar plantilla ${template.name}`}
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(template)}
                              className="text-red-600 hover:text-red-900"
                              title={`Eliminar plantilla ${template.name}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Diálogos de confirmación */}
      <ConfirmDialog
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) {
            onDelete(deleteConfirm.id);
            setDeleteConfirm(null);
          }
        }}
        title="Eliminar plantilla"
        message={`¿Estás seguro de que quieres eliminar la plantilla "${deleteConfirm?.name}"? Esta acción no se puede deshacer.`}
      />

      <ConfirmDialog
        isOpen={copyingTemplate !== null}
        onClose={() => setCopyingTemplate(null)}
        onConfirm={handleConfirmCopy}
        title="Copiar plantilla"
        confirmText="Copiar"
        cancelText="Cancelar"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="copyName" className="block text-sm font-medium text-gray-700">
              Nombre de la nueva plantilla
            </label>
            <input
              id="copyName"
              type="text"
              value={copyFormData.name}
              onChange={e => setCopyFormData(prev => ({ ...prev, name: e.target.value }))}
              className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="copyLevel" className="block text-sm font-medium text-gray-700">
              Nivel
            </label>
            <CustomSelect
              id="copyLevel"
              value={copyFormData.levelId}
              onValueChange={(v) => setCopyFormData(prev => ({ ...prev, levelId: v }))}
              options={levels.map(l => ({ value: l.id, label: l.name }))}
              placeholder="Selecciona un nivel"
            />
          </div>
        </div>
      </ConfirmDialog>

      {/* Modal de matriz asignaturas × pueblos */}
      {matrixTemplateId && (() => {
        const template = templates.find(t => t.id === matrixTemplateId);
        if (!template) return null;
        return (
          <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-40">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
              <div className="flex justify-between items-center px-6 py-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Asignaturas por pueblo — {template.name}</h3>
                <button onClick={() => setMatrixTemplateId(null)} className="p-1 text-gray-400 rounded hover:text-gray-600 hover:bg-gray-100">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="overflow-auto flex-1 p-6">
                {pueblos.length === 0 ? (
                  <p className="text-sm text-gray-500">No hay pueblos configurados. Añádelos en Ajustes → Pueblos del CRA.</p>
                ) : (
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr>
                        <th className="py-2 pr-4 text-left text-gray-700">Asignatura</th>
                        {pueblos.map(p => (
                          <th key={p.id} className="px-3 py-2 text-center text-gray-700">{p.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {template.subjects.map(subject => (
                        <tr key={subject.id} className="border-t hover:bg-gray-50">
                          <td className="py-2 pr-4 font-medium text-gray-800">
                            {subject.name}
                            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                              subject.type === 'individual' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'
                            }`}>{subject.type === 'individual' ? 'Individual' : 'Grupal'}</span>
                          </td>
                          {pueblos.map(pueblo => (
                            <td key={pueblo.id} className="px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={matrixAssignments.some(a => a.subjectId === subject.id && a.puebloId === pueblo.id)}
                                onChange={() => toggleAssignment(subject.id, pueblo.id)}
                                className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="flex gap-3 justify-end px-6 py-4 bg-gray-50 rounded-b-xl border-t">
                <button
                  onClick={() => setMatrixTemplateId(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveMatrix}
                  disabled={matrixSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {matrixSaving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
} 